import type { RefObject } from 'react';
import type { WorkbookInstance } from '@fortune-sheet/react';
import Modal from '../../_custom/Modal/Modal';
import Button from '../../_custom/Button/Button';
import Checkbox from '../../_custom/Checkbox/Checkbox';
import { useFormatCellsDialog, type FormatCellsCategory } from './useFormatCellsDialog';
import FormatCellsIcon from '../../../assets/Svg/FormatCellsIcon';
import modalOverDrawerStyles from '../../ConfirmDialog/modalOverDrawer.module.scss';
import styles from './FormatCellsDialog.module.scss';

export interface FormatCellsDialogProps {
    open: boolean;
    onClose: () => void;
    workbookRef: RefObject<WorkbookInstance>;
}

const CATEGORIES: FormatCellsCategory[] = [
    'General',
    'Number',
    'Currency',
    'Accounting',
    'Percentage',
    'Scientific',
    'Date',
    'Time',
    'Text',
    'Custom',
];

/**
 * Ctrl/Cmd+1's Number-format dialog. Presentational — every
 * category assembles a format string (via `buildNumFmt`/`EXCEL_NUMBER_PRESETS`,
 * or the free-text Custom field) which is dual-engine-validated
 * (`isSafeNumFmt`) before Apply is ever enabled — nothing reaches the reducer
 * that could throw. State/derivation/Apply live in `useFormatCellsDialog`.
 */
const FormatCellsDialog: React.FC<FormatCellsDialogProps> = ({ open, onClose, workbookRef }) => {
    const {
        category,
        setCategory,
        decimals,
        setDecimals,
        thousands,
        setThousands,
        negative,
        setNegative,
        dateTimeFmt,
        setDateTimeFmt,
        custom,
        setCustom,
        datePresets,
        timePresets,
        fa,
        valid,
        preview,
        handleApply,
    } = useFormatCellsDialog({ open, workbookRef, onClose });

    return (
        <Modal open={open} onClose={onClose} size="flexible" className={modalOverDrawerStyles.modalOverDrawer} withoutCloseButton>
            <div className={styles.Dialog}>
                <span className={styles.Title}>Format cells</span>
                {/* This and the other two <select>s below stay native, not _custom/Dropdown — Dropdown transitively imports store/investor/reducer.ts, which breaks File.test.tsx/Folder.test.tsx's partial jest.mock of store/investor/constants. */}
                <div className={styles.Row}>
                    <span className={styles.Label}>Category</span>
                    <select
                        className={styles.Select}
                        aria-label="Category"
                        value={category}
                        onChange={(e) => setCategory(e.target.value as FormatCellsCategory)}
                    >
                        {CATEGORIES.map((c) => (
                            <option key={c} value={c}>
                                {c}
                            </option>
                        ))}
                    </select>
                </div>

                {(category === 'Number' || category === 'Currency' || category === 'Percentage' || category === 'Scientific') && (
                    <div className={styles.Row}>
                        <span className={styles.Label}>Decimals</span>
                        <input
                            type="number"
                            min={0}
                            max={30}
                            className={styles.Input}
                            aria-label="Decimals"
                            value={decimals}
                            onChange={(e) => setDecimals(Math.min(30, Math.max(0, Number(e.target.value) || 0)))}
                        />
                    </div>
                )}
                {category === 'Number' && (
                    <>
                        <Checkbox label="Use 1000 separator" checked={thousands} onChange={setThousands} />
                        <div className={styles.Row}>
                            <span className={styles.Label}>Negative numbers</span>
                            <select
                                className={styles.Select}
                                aria-label="Negative numbers"
                                value={negative}
                                onChange={(e) => setNegative(e.target.value as typeof negative)}
                            >
                                <option value="minus">-1,234.50</option>
                                <option value="red">-1,234.50 (red)</option>
                                <option value="paren">(1,234.50)</option>
                                <option value="red-paren">(1,234.50) (red)</option>
                            </select>
                        </div>
                    </>
                )}
                {(category === 'Date' || category === 'Time') && (
                    <div className={styles.Row}>
                        <span className={styles.Label}>Format</span>
                        <select
                            className={styles.Select}
                            aria-label="Date/time format"
                            value={dateTimeFmt}
                            onChange={(e) => setDateTimeFmt(e.target.value)}
                        >
                            {(category === 'Date' ? datePresets : timePresets).map((p) => (
                                <option key={p} value={p}>
                                    {p}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                {category === 'Custom' && (
                    <div className={styles.Row}>
                        <span className={styles.Label}>Format code</span>
                        <input
                            className={styles.Input}
                            value={custom}
                            onChange={(e) => setCustom(e.target.value)}
                            placeholder="e.g. 0.0%"
                            aria-label="Custom number format"
                        />
                    </div>
                )}

                {valid ? (
                    <div className={styles.Preview}>Preview: {preview || fa}</div>
                ) : (
                    <span className={styles.Error}>This number format isn&apos;t valid.</span>
                )}

                <div className={modalOverDrawerStyles.actions}>
                    <Button text="Cancel" variant="purpleinverted" onClick={onClose} className={modalOverDrawerStyles.actionBtn} />
                    <Button
                        text="Apply"
                        variant="primary"
                        onClick={handleApply}
                        disabled={!valid}
                        className={modalOverDrawerStyles.actionBtn}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default FormatCellsDialog;

/** Toolbar "Format cells…" item (pattern: useColumnAutofit.tsx's
 * fitColumnsToolbarItem). */
export const formatCellsToolbarItem = (onOpen: () => void) => ({
    key: 'format-cells',
    tooltip: 'Format cells…',
    icon: <FormatCellsIcon />,
    onClick: onOpen,
});
